// frontend/src/components/dedup/SourceManager.tsx
import { getDedupUrl, getIngestUrl, authFetch } from '../../config';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward,
  ArrowDownward,
  Storage as StorageIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

interface Source {
  id: number;
  source_type: 'external' | 'internal';
  source_id: number | null;
  priority: number;
  description: string;
  record_count: number;
  status: string;
}

interface ExternalSource {
  id: number;
  name: string;
  total_rows: number;
  status: string;
}

interface Props {
  campaignId: number;
  sources: Source[];
  onSourcesChange: (sources: Source[]) => void;
}

export default function SourceManager({ campaignId, sources, onSourcesChange }: Props) {
  const [open, setOpen] = useState(false);
  const [externalSources, setExternalSources] = useState<ExternalSource[]>([]);
  const [newSource, setNewSource] = useState({
    source_type: 'external' as 'external' | 'internal',
    source_id: null as number | null,
    description: ''
  });
  const [internalFilters, setInternalFilters] = useState({
    lottery_type: '',
    status: 'active',
    opt_in_telefon: false,
    opt_in_email: false
  });

  useEffect(() => {
    loadExternalSources();
    if (campaignId) {
      loadCampaignSources();
    }
  }, [campaignId]);

  const loadExternalSources = async () => {
    try {
      const response = await authFetch(getIngestUrl('/external/sources'));
      const result = await response.json();
      if (result.success) {
        setExternalSources(result.sources);
      }
    } catch (error) {
      console.error('Error loading external sources:', error);
    }
  };

  const loadCampaignSources = async () => {
    console.log('Loading sources for campaign:', campaignId);  // DEBUG
    try {
      const response = await authFetch(getDedupUrl(`/campaigns/${campaignId}/sources`));
      const result = await response.json();
      console.log('Sources loaded:', result);  // DEBUG
      if (result.success) {
        onSourcesChange(result.sources);
      }
    } catch (error) {
      console.error('Error loading campaign sources:', error);
    }
  };

  const handleAddSource = async () => {
    if (!campaignId) return;

    const payload = {
      ...newSource,
      priority: sources.length + 1,
      record_count: 0
    };

    try {
      const response = await authFetch(getDedupUrl(`/campaigns/${campaignId}/sources`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (result.success) {
        await loadCampaignSources();
        setOpen(false);
        setNewSource({ source_type: 'external', source_id: null, description: '' });
      }
    } catch (error) {
      console.error('Error adding source:', error);
    }
  };

  const handleDeleteSource = async (sourceId: number) => {
    if (!window.confirm('Quelle wirklich entfernen?')) return;

    try {
      await authFetch(getDedupUrl(`/sources/${sourceId}`), {
        method: 'DELETE'
      });
      await loadCampaignSources();
    } catch (error) {
      console.error('Error deleting source:', error);
    }
  };

  const handleMovePriority = async (sourceId: number, direction: 'up' | 'down') => {
    const currentIndex = sources.findIndex(s => s.id === sourceId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= sources.length) return;

    const newSources = [...sources];
    [newSources[currentIndex], newSources[newIndex]] = [newSources[newIndex], newSources[currentIndex]];
    
    // Update priorities
    newSources.forEach((source, idx) => {
      source.priority = idx + 1;
    });

    try {
      await authFetch(getDedupUrl(`/campaigns/${campaignId}/sources/reorder`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          sources: newSources.map(s => ({ id: s.id, priority: s.priority }))
        })
      });
      onSourcesChange(newSources);
    } catch (error) {
      console.error('Error reordering sources:', error);
    }
  };

  const getPriorityBadgeColor = (priority: number) => {
    if (priority === 1) return 'error';
    if (priority === 2) return 'warning';
    if (priority === 3) return 'info';
    return 'default';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">
          Datenquellen & Prioritäten
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Quelle hinzufügen
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Prioritäts-Reihenfolge:</strong> Bei Duplikaten wird der Datensatz aus der Quelle 
        mit der höheren Priorität (niedrigere Nummer) verwendet.
      </Alert>

      {sources.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <StorageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Noch keine Quellen hinzugefügt
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Fügen Sie externe Dateien oder interne Spieler-Daten hinzu
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={80}>Priorität</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Beschreibung</TableCell>
                <TableCell align="right">Datensätze</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.map((source, index) => (
                <TableRow key={source.id}>
                  <TableCell>
                    <Chip 
                      label={`#${source.priority}`}
                      color={getPriorityBadgeColor(source.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {source.source_type === 'external' ? (
                        <>
                          <CloudUploadIcon fontSize="small" color="primary" />
                          <Typography variant="body2">Externe Datei</Typography>
                        </>
                      ) : (
                        <>
                          <StorageIcon fontSize="small" color="secondary" />
                          <Typography variant="body2">Interne Daten</Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{source.description}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {source.record_count.toLocaleString('de-DE')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={source.status} 
                      color={source.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleMovePriority(source.id, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUpward />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMovePriority(source.id, 'down')}
                      disabled={index === sources.length - 1}
                    >
                      <ArrowDownward />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteSource(source.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Source Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Quelle hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Quellen-Typ</InputLabel>
              <Select
                value={newSource.source_type}
                onChange={(e) => setNewSource({ 
                  ...newSource, 
                  source_type: e.target.value as 'external' | 'internal',
                  source_id: null 
                })}
                label="Quellen-Typ"
              >
                <MenuItem value="external">Externe Datei</MenuItem>
                <MenuItem value="internal">Interne Spieler-Daten</MenuItem>
              </Select>
            </FormControl>

            {newSource.source_type === 'external' ? (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Externe Datei auswählen</InputLabel>
                <Select
                  value={newSource.source_id || ''}
                  onChange={(e) => {
                    const sourceId = Number(e.target.value);
                    const source = externalSources.find(s => s.id === sourceId);
                    setNewSource({ 
                      ...newSource, 
                      source_id: sourceId,
                      description: source?.name || ''
                    });
                  }}
                  label="Externe Datei auswählen"
                >
                  {externalSources.map(source => (
                    <MenuItem key={source.id} value={source.id}>
                      {source.name} ({source.total_rows.toLocaleString('de-DE')} Datensätze)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Lotterie-Typ</InputLabel>
                  <Select
                    value={internalFilters.lottery_type}
                    onChange={(e) => setInternalFilters({ 
                      ...internalFilters, 
                      lottery_type: e.target.value 
                    })}
                    label="Lotterie-Typ"
                  >
                    <MenuItem value="">Alle</MenuItem>
                    <MenuItem value="SKL">SKL</MenuItem>
                    <MenuItem value="NKL">NKL</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={internalFilters.status}
                    onChange={(e) => setInternalFilters({ 
                      ...internalFilters, 
                      status: e.target.value 
                    })}
                    label="Status"
                  >
                    <MenuItem value="all">Alle</MenuItem>
                    <MenuItem value="active">Aktiv</MenuItem>
                    <MenuItem value="inactive">Inaktiv</MenuItem>
                  </Select>
                </FormControl>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Filter werden beim Hinzufügen gespeichert und auf interne Daten angewendet
                </Alert>
              </>
            )}

            <TextField
              fullWidth
              label="Beschreibung"
              value={newSource.description}
              onChange={(e) => setNewSource({ ...newSource, description: e.target.value })}
              helperText="Optionale Beschreibung für diese Quelle"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleAddSource} 
            variant="contained"
            disabled={newSource.source_type === 'external' && !newSource.source_id}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}